import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn, getOptimizedImageUrl } from '../lib/utils';

const slides = [
  {
    image: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/content-1_1.jpg',
    title: 'Autoriseret kloakmester med over 20 års erfaring',
    alt: 'Jan Hedegaard, autoriseret kloakmester foran firmabilen i Galten',
    position: 'object-[75%_center]',
    subtitle: 'Hos Entreprenør Jan Hedegaard udfører vi kloakarbejde, kloakservice og entreprenøropgaver for private og erhverv.',
    cta: 'Få et uforpligtende tilbud',
    href: '/kontakt'
  },
  {
    image: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/1126263a-79e8-4e3f-8c49-a4732d1dc0fa.jpg',
    title: 'Kloakservice & Kloakseparering',
    alt: 'Mandskab ved inspektion og kontrol af kloakrør i udgravning',
    position: 'object-center',
    subtitle: 'Grundig fejlfinding med TV-inspektion, rørforløb og adskillelse af regn- og spildevand efter gældende regler.',
    cta: 'Læs om kloakservice',
    href: '/kloakservice'
  },
  {
    image: 'https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/cfedd232-70ee-41da-a16f-a4d21d9510cb.jpg',
    title: 'Én entreprenør til hele opgaven',
    alt: 'Medarbejder ved udgravning og rørlægning med minigraver i baggrunden',
    position: 'object-center',
    subtitle: 'Fra kloak og udgravning til fundamenter, dræn, faskiner og færdig reetablering af have og indkørsel.',
    cta: 'Se vores arbejde',
    href: '/galleri'
  }
];

export default function Hero() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const next = () => setCurrent((prev) => (prev + 1) % slides.length);
  const prev = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section className="relative h-screen min-h-[600px] w-full overflow-hidden bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 bg-stone-950/40 z-10" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/75 via-stone-950/35 to-stone-950/10 z-15" />
          <img
            src={getOptimizedImageUrl(slides[current].image, 1600)}
            alt={slides[current].alt || slides[current].title}
            className={cn(
              "h-full w-full object-cover",
              slides[current].position || "object-center"
            )}
          />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-20 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            key={current + '-content'}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="max-w-3xl bg-stone-950/40 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-white/10 mt-12"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-1.5 rounded-full text-sm font-semibold border border-orange-500/30 mb-6">
              <ShieldCheck className="w-4 h-4 text-orange-500" />
              <span>Autoriseret Kloakmester</span>
            </div>
            <p className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              {slides[current].title}
            </p>
            <p className="text-xl text-stone-200 mb-10 max-w-2xl leading-relaxed drop-shadow-md">
              {slides[current].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={slides[current].href}
                className="bg-orange-600 text-white px-8 py-4 rounded-md text-lg font-bold hover:bg-orange-700 transition-all shadow-xl shadow-orange-900/40 flex items-center justify-center gap-2"
              >
                {slides[current].cta}
                <ChevronRight className="w-5 h-5" />
              </Link>
              <Link
                to="/om-os"
                className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-8 py-4 rounded-md text-lg font-bold hover:bg-white/20 transition-all flex items-center justify-center"
              >
                Læs om os
              </Link>
            </div>

            {/* 3byggetilbud Trust Badge */}
            <div className="mt-6 pt-5 border-t border-white/15 flex flex-wrap items-center gap-2 text-sm text-stone-200">
              <span className="text-base" role="img" aria-label="Stjerne">⭐</span>
              <span>
                <strong className="text-white font-semibold">5 / 5 stjerner</strong> baseret på anmeldelser på{' '}
                <a
                  href="https://www.3byggetilbud.dk/leverandoer/entreprenr-jan-hedegaard-aps/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-orange-400 font-semibold underline underline-offset-2 hover:text-orange-300 transition-colors"
                >
                  3byggetilbud.dk
                </a>
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-10 right-10 z-30 flex gap-4">
        <button
          onClick={prev}
          className="p-3 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={next}
          className="p-3 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Indicators */}
      <div className="absolute bottom-10 left-10 z-30 flex gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={cn(
              "h-1 transition-all duration-300 rounded-full",
              current === i ? "w-12 bg-orange-500" : "w-6 bg-white/30"
            )}
          />
        ))}
      </div>
    </section>
  );
}
